/**
 * Alpine.js CSP-Safe Components
 *
 * All Alpine components must be registered here via Alpine.data() for CSP compliance.
 * The CSP Alpine build does not support inline JS expressions in x-data attributes.
 *
 * Organized by domain:
 * - Reusable components (toggleBlock, clipboardCopy, etc.)
 * - Layout components (dashboardLayout, globalSearchModal, etc.)
 * - Chat components (chatMessages, chatInput, etc.)
 * - Page-specific components
 */

/**
 * Probe whether the SageTube Chrome extension is installed.
 * Requires the extension to expose icons/icon16.png as a web-accessible resource
 * for sagetube.ai. Result is cached in sessionStorage for the tab lifetime.
 */
async function probeExtension(extensionId) {
    const key = 'sagetube_ext_detected';
    const cached = sessionStorage.getItem(key);
    if (cached !== null) return cached === '1';
    try {
        await fetch(`chrome-extension://${extensionId}/icons/icon16.png`, { mode: 'no-cors' });
        sessionStorage.setItem(key, '1');
        return true;
    } catch {
        sessionStorage.setItem(key, '0');
        return false;
    }
}

document.addEventListener('alpine:init', () => {

    // =========================================================================
    // Reusable Components
    // =========================================================================

    /**
     * Simple boolean toggle (open/close, expand/collapse).
     * Usage: x-data="toggleBlock"
     *        @click="toggle" x-show="open"
     */
    Alpine.data('toggleBlock', () => ({
        open: false,
        toggle() {
            this.open = !this.open;
        },
        close() {
            this.open = false;
        },
    }));

    /**
     * Hover tooltip — show on mouseenter, hide on mouseleave.
     * Usage: x-data="hoverTooltip"
     *        @mouseenter="show" @mouseleave="hide" x-show="visible"
     */
    Alpine.data('hoverTooltip', () => ({
        visible: false,
        show() {
            this.visible = true;
        },
        hide() {
            this.visible = false;
        },
    }));

    /**
     * Copy text to clipboard with "Copied!" feedback.
     * Usage: x-data="clipboardCopy"
     *        @click="copy" x-show="!copied" / x-show="copied"
     *        Reads text from data-copy-text attribute on the component root.
     */
    Alpine.data('clipboardCopy', () => ({
        copied: false,
        copy() {
            const text = this.$root.dataset.copyText;
            if (text) {
                navigator.clipboard.writeText(text).then(() => {
                    this.copied = true;
                    setTimeout(() => this.copied = false, 2000);
                });
            }
        },
    }));

    /**
     * Loading link — show spinner on click.
     * Usage: x-data="loadingLink"
     *        @click="start" x-show="loading"
     */
    Alpine.data('loadingLink', () => ({
        loading: false,
        start() {
            this.loading = true;
        },
    }));

    /**
     * Live polling with restart on events.
     * Usage: x-data="livePolling"
     * Reads poll interval from data-poll-interval (default 5000ms).
     * Listens for 'newMessage' and 'refreshDataNow' window events.
     */
    Alpine.data('livePolling', () => ({
        pollingInterval: null,

        init() {
            this.startPolling();

            this._onNewMessage = () => {
                this.stopPolling();
                setTimeout(() => this.startPolling(), 5000);
            };
            this._onRefreshNow = () => {
                this.stopPolling();
                this.$wire.refreshData();
                this.startPolling();
            };

            window.addEventListener('newMessage', this._onNewMessage);
            window.addEventListener('refreshDataNow', this._onRefreshNow);
        },

        startPolling() {
            const interval = parseInt(this.$el.dataset.pollInterval) || 5000;
            this.pollingInterval = setInterval(() => {
                this.$wire.refreshData();
            }, interval);
        },

        stopPolling() {
            clearInterval(this.pollingInterval);
        },

        destroy() {
            this.stopPolling();
            window.removeEventListener('newMessage', this._onNewMessage);
            window.removeEventListener('refreshDataNow', this._onRefreshNow);
        },
    }));

    /**
     * Auto mark-as-read on intersection (notifications).
     * Usage: x-data="autoMarkRead"
     * Reads notification ID from data-notification-id attribute.
     */
    Alpine.data('autoMarkRead', () => ({
        timeoutId: null,

        enterView() {
            const id = parseInt(this.$el.dataset.notificationId);
            this.timeoutId = setTimeout(() => {
                this.$wire.markAsRead(id);
            }, 1500);
        },

        leaveView() {
            clearTimeout(this.timeoutId);
        },
    }));

    /**
     * Flash message with auto-dismiss after timeout.
     * Usage: x-data="flashMessage"
     */
    Alpine.data('flashMessage', () => ({
        timeoutId: null,

        init() {
            this.scheduleDismiss();
            this._onNewMessage = () => {
                clearTimeout(this.timeoutId);
                this.scheduleDismiss();
            };
            window.addEventListener('newMessage', this._onNewMessage);
        },

        scheduleDismiss() {
            this.timeoutId = setTimeout(() => {
                this.$el.innerHTML = '';
            }, 10000);
        },

        destroy() {
            clearTimeout(this.timeoutId);
            window.removeEventListener('newMessage', this._onNewMessage);
        },
    }));

    /**
     * Countdown timer (e.g., cooldown button).
     * Usage: x-data="cooldownTimer"
     * Reads initial seconds from data-seconds attribute (default 60).
     * Refreshes the Livewire component when countdown reaches 0.
     */
    Alpine.data('cooldownTimer', () => ({
        seconds: 60,

        init() {
            this.seconds = parseInt(this.$el.dataset.seconds) || 60;
            const t = setInterval(() => {
                this.seconds--;
                if (this.seconds <= 0) {
                    clearInterval(t);
                    this.$wire.$refresh();
                }
            }, 1000);
        },
    }));

    /**
     * Transcript viewer with toggle and clipboard copy.
     * Usage: x-data="transcriptViewer"
     * Reads initial view from data-show-transcript="true|false".
     */
    Alpine.data('transcriptViewer', () => ({
        showTranscript: false,
        copiedSummary: false,
        copiedTranscript: false,

        init() {
            this.showTranscript = this.$el.dataset.showTranscript === 'true';
        },

        toggleView() {
            this.showTranscript = !this.showTranscript;
        },

        viewLabel() {
            return this.showTranscript ? 'View Summary' : 'View Transcript';
        },

        copySummary() {
            navigator.clipboard.writeText(this.$refs.summaryContent.innerText).then(() => {
                this.copiedSummary = true;
                setTimeout(() => this.copiedSummary = false, 2000);
            });
        },

        copyTranscript() {
            navigator.clipboard.writeText(this.$refs.transcriptContent.innerText).then(() => {
                this.copiedTranscript = true;
                setTimeout(() => this.copiedTranscript = false, 2000);
            });
        },
    }));

    // =========================================================================
    // Layout Components
    // =========================================================================

    /**
     * Dashboard layout — dark mode with localStorage, preloader, modals.
     * Usage: x-data="dashboardLayout"
     */
    Alpine.data('dashboardLayout', () => ({
        page: 'ecommerce',
        loaded: true,
        darkMode: true,
        stickyMenu: false,
        scrollTop: false,
        showRegisterModal: false,
        showTermsModal: false,
        termsAccepted: false,

        init() {
            this.darkMode = JSON.parse(localStorage.getItem('darkMode'));
            this.$watch('darkMode', value => localStorage.setItem('darkMode', JSON.stringify(value)));
            setTimeout(() => this.loaded = false, 500);
        },

        toggleDarkMode() {
            this.darkMode = !this.darkMode;
        },

        openRegisterModal() {
            this.showRegisterModal = true;
        },

        closeRegisterModal() {
            this.showRegisterModal = false;
        },

        closeTermsModal() {
            this.showTermsModal = false;
        },
    }));

    /**
     * Global search modal with keyboard shortcuts.
     * Usage: x-data="globalSearchModal"
     */
    Alpine.data('globalSearchModal', () => ({
        searchOpen: false,

        init() {
            this._onOpenSearch = () => {
                this.searchOpen = true;
                this.$nextTick(() => this.$refs.globalSearch.focus());
            };
            this._onSlash = (e) => {
                if (!this.searchOpen && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
                    e.preventDefault();
                    this.searchOpen = true;
                    this.$nextTick(() => this.$refs.globalSearch.focus());
                }
            };
            this._onEscape = () => { this.searchOpen = false; };

            window.addEventListener('open-search-modal', this._onOpenSearch);
            window.addEventListener('keydown', (e) => {
                if (e.key === '/') this._onSlash(e);
                if (e.key === 'Escape') this._onEscape();
            });
        },

        close() {
            this.searchOpen = false;
        },
    }));

    /**
     * Notification bell with MutationObserver for pulse animation.
     * Usage: x-data="notificationBell"
     */
    Alpine.data('notificationBell', () => ({
        isNoticeVisible: false,
        bellPulse: false,
        lastCount: 0,

        init() {
            const observer = new MutationObserver(() => {
                const counter = this.$el.querySelector('.notice-count');
                const newCount = counter ? parseInt(counter.textContent) || 0 : 0;
                if (newCount > this.lastCount && this.lastCount > 0) {
                    this.bellPulse = true;
                    setTimeout(() => this.bellPulse = false, 500);
                }
                this.lastCount = newCount;
            });
            observer.observe(this.$el, { childList: true, subtree: true, characterData: true });
        },

        toggleNotice() {
            this.isNoticeVisible = !this.isNoticeVisible;
        },

        closeNotice() {
            this.isNoticeVisible = false;
        },
    }));

    /**
     * Section navigation with IntersectionObserver.
     * Usage: x-data="sectionNav"
     */
    Alpine.data('sectionNav', () => ({
        active: '',

        init() {
            ['how-it-works', 'features', 'pulse', 'pricing'].forEach(id => {
                const el = document.getElementById(id);
                if (!el) return;
                new IntersectionObserver(
                    ([entry]) => { if (entry.isIntersecting) this.active = id; },
                    { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
                ).observe(el);
            });
        },

        setActive() {
            this.active = this.$el.dataset.section;
        },

        isActive() {
            return this.active === this.$el.dataset.section;
        },
    }));

    /**
     * Help center client-side search filtering.
     * Usage: x-data="helpSearch"
     */
    Alpine.data('helpSearch', () => ({
        search: '',
    }));

    // =========================================================================
    // Chat Components
    // =========================================================================

    /**
     * Chat messages area — scroll management, bot thinking state.
     * Usage: x-data="chatMessages"
     * Reads scroll delay from data-scroll-delay attribute (default 500ms).
     */
    Alpine.data('chatMessages', () => ({
        timeoutId: null,
        botThinking: false,
        loaded: false,

        init() {
            const box = this.$el;
            const delay = parseInt(this.$el.dataset.scrollDelay) || 500;

            setTimeout(() => {
                box.scrollTop = box.scrollHeight;
                this.loaded = true;
            }, delay);

            window.addEventListener('scrollToBottom', () => {
                this.botThinking = false;
                const textareaQuestion = document.getElementById('textareaQuestion');
                if (textareaQuestion) {
                    this.$nextTick(() => textareaQuestion.focus());
                }
                clearTimeout(this.timeoutId);
                this.timeoutId = setTimeout(() => { box.scrollTop = box.scrollHeight; }, 300);
            });

            window.addEventListener('AppLivewireChat_geminiGetAnswer', () => {
                this.botThinking = true;
            });

            window.addEventListener('AppLivewireChat_answerFailed', () => {
                this.botThinking = false;
                const textareaQuestion = document.getElementById('textareaQuestion');
                if (textareaQuestion) {
                    this.$nextTick(() => textareaQuestion.focus());
                }
            });

            /**
             * SSE streaming path (EXPERT_ANSWER_SSE=true).
             *
             * Livewire dispatches 'expert-stream-open' after sendQuestion() mints a
             * ticket. We open an EventSource (no auto-reconnect), pipe token events
             * into #expert-stream-target, and on done fire Livewire's expertStreamDone
             * so the component re-reads the persisted conversation from the DB.
             *
             * Security: EventSource is one-shot (closed on done/error). The ticket is
             * 30 s TTL and single-use; we never re-open on network error.
             */
            window.addEventListener('expert-stream-open', (e) => {
                const { ticket, q, url } = e.detail;
                const streamUrl = url + '?ticket=' + encodeURIComponent(ticket) + '&q=' + encodeURIComponent(q);
                const target = document.getElementById('expert-stream-target');

                if (target) {
                    target.textContent = '';
                }

                const es = new EventSource(streamUrl);

                es.addEventListener('token', (ev) => {
                    try {
                        const data = JSON.parse(ev.data);
                        if (target && typeof data.t === 'string') {
                            target.textContent += data.t;
                            // Scroll to keep the streaming text in view
                            box.scrollTop = box.scrollHeight;
                        }
                    } catch {
                        // ignore malformed token frames
                    }
                });

                es.addEventListener('done', () => {
                    es.close();
                    Livewire.dispatch('expert-stream-done');
                });

                es.addEventListener('error', () => {
                    es.close();
                    this.botThinking = false;
                    const textareaQuestion = document.getElementById('textareaQuestion');
                    if (textareaQuestion) {
                        this.$nextTick(() => textareaQuestion.focus());
                    }
                    // Show a transient error bubble consistent with existing error handling
                    window.dispatchEvent(new CustomEvent('AppLivewireChat_answerFailed'));
                });
            });
        },
    }));

    /**
     * Rotating caption next to the thinking dots — gives perceived progress
     * during long RAG round-trips. Advances through data-captions and stays
     * on the last one; parent block is aria-hidden so it never spams SRs.
     * Usage: x-data="thinkingCaption" data-captions='["...", "..."]'
     */
    Alpine.data('thinkingCaption', () => ({
        captions: [],
        step: 0,
        timer: null,

        get caption() {
            return this.captions[this.step] || '';
        },

        init() {
            try {
                this.captions = JSON.parse(this.$el.dataset.captions || '[]');
            } catch {
                this.captions = [];
            }

            window.addEventListener('AppLivewireChat_geminiGetAnswer', () => {
                this.step = 0;
                clearInterval(this.timer);
                this.timer = setInterval(() => {
                    if (this.step < this.captions.length - 1) {
                        this.step++;
                    } else {
                        clearInterval(this.timer);
                    }
                }, 4000);
            });

            const stop = () => {
                clearInterval(this.timer);
                this.step = 0;
            };
            window.addEventListener('scrollToBottom', stop);
            window.addEventListener('AppLivewireChat_answerFailed', stop);
        },
    }));

    /**
     * Chat input form — textarea auto-resize, enter-to-send, bot thinking state.
     * Usage: x-data="chatInput"
     * Reads focus delay from data-focus-delay attribute (default 500ms).
     */
    Alpine.data('chatInput', () => ({
        botThinking: false,

        init() {
            window.addEventListener('AppLivewireChat_geminiGetAnswer', () => {
                this.botThinking = true;
            });
            window.addEventListener('scrollToBottom', () => {
                this.botThinking = false;
            });
            window.addEventListener('AppLivewireChat_answerFailed', () => {
                this.botThinking = false;
            });
        },

        setupTextarea() {
            const el = this.$el;
            const delay = parseInt(el.closest('[data-focus-delay]')?.dataset.focusDelay) || 500;
            setTimeout(() => el.focus(), delay);
            el.style.height = 'auto';
            el.addEventListener('input', function () {
                this.style.height = 'auto';
                this.style.height = Math.min(this.scrollHeight, 120) + 'px';
            });
        },

        sendOnEnter() {
            const event = this.$event;
            if (event.shiftKey) {
                return; // allow newline when bound without .prevent
            }
            event.preventDefault();
            if (this.botThinking) {
                return;
            }
            // Submit the form rather than naming a Livewire method: the form's
            // wire:submit already knows what this composer does, and on the Ask
            // page that changes with the mode. Calling sendQuestion() directly
            // meant Enter asked an ordinary question while the visitor had
            // "Porównaj źródła" switched on (found 2026-08-25), and it would
            // silently do the same for any mode added later.
            const form = this.$el instanceof HTMLFormElement ? this.$el : this.$el.closest('form');
            if (form && typeof form.requestSubmit === 'function') {
                form.requestSubmit();

                return;
            }
            this.$wire.sendQuestion();
        },
    }));

    /**
     * Chat layout with sidebar entangle.
     * Usage: x-data="chatLayout"
     */
    Alpine.data('chatLayout', () => ({
        sidebarOpen: false,

        isMobile() {
            return window.matchMedia('(max-width: 767px)').matches;
        },

        closeSidebarOnMobile() {
            if (this.isMobile()) {
                this.sidebarOpen = false;
            }
        },

        init() {
            this.sidebarOpen = this.$wire.$entangle('sidebarOpen');

            // On phones the sidebar is an overlay drawer — start it closed so it
            // doesn't cover the chat on first load (it defaults open for desktop).
            this.$nextTick(() => this.closeSidebarOnMobile());

            // Picking a conversation or starting a new one should dismiss the
            // drawer on mobile so the chat is visible again.
            this._onSidebarAction = () => this.closeSidebarOnMobile();
            window.addEventListener('conversationSelected', this._onSidebarAction);
            window.addEventListener('newConversation', this._onSidebarAction);
        },

        destroy() {
            window.removeEventListener('conversationSelected', this._onSidebarAction);
            window.removeEventListener('newConversation', this._onSidebarAction);
        },
    }));

    /**
     * Shared chat input with referral tracking and first question analytics.
     * Usage: x-data="sharedChatInput"
     * Reads config from data-* attributes.
     */
    Alpine.data('sharedChatInput', () => ({
        firstQuestionTracked: false,

        init() {
            this._referralSource = this.$el.dataset.referralSource || '';
            this._referralCategory = this.$el.dataset.referralCategory || '';
            this._expertToken = this.$el.dataset.expertToken || '';
            this._expertTitle = this.$el.dataset.expertTitle || '';
            this._expertCategory = this.$el.dataset.expertCategory || '';
            this._sourceClickUrl = this.$el.dataset.sourceClickUrl || '';

            // Aggregate day×video click-through counter (cookie-less; feeds the
            // creator digest). sendBeacon so the navigation is never delayed.
            if (this._sourceClickUrl) {
                const beacon = (event) => {
                    const anchor = event.target.closest('a[href*="youtube.com/watch"]');
                    if (!anchor) return;
                    const videoId = new URL(anchor.href).searchParams.get('v');
                    if (!videoId || !/^[A-Za-z0-9_-]{11}$/.test(videoId)) return;
                    navigator.sendBeacon(this._sourceClickUrl, new URLSearchParams({ video_id: videoId }));
                };
                this.$el.addEventListener('click', beacon);
                this.$el.addEventListener('auxclick', beacon);
            }
        },

        trackFirstQuestion() {
            const question = this.$refs.questionInput ? this.$refs.questionInput.value.trim() : '';
            if (this._referralSource !== 'explore' || this.firstQuestionTracked || question === '' || typeof window.sageTrack !== 'function') {
                return;
            }
            this.firstQuestionTracked = true;
            window.sageTrack('explore_first_question_sent', {
                expert_token: this._expertToken,
                expert_title: this._expertTitle,
                category: this._expertCategory,
                landing_category: this._referralCategory,
            });
        },
    }));

    /**
     * Ask-page conversation history, kept only in the visitor's own browser.
     *
     * The chat always opens empty (SharedChatBox::openFreshConversation), so this
     * is what makes an earlier transcript reachable again. Entries hold role +
     * plain text only: the server rebuilds anything restored from here and never
     * accepts citation metadata back, because that path renders raw HTML.
     *
     * Usage: x-data="askHistory"
     *   data-storage-key   localStorage key (per Expert)
     *   data-carry-over    JSON transcript this page load retired (optional)
     */
    Alpine.data('askHistory', () => ({
        open: false,
        items: [],

        init() {
            this._key = this.$el.dataset.storageKey || 'sagetube:ask-history';
            this.items = this.readItems();

            this.archiveCarryOver(this.$el.dataset.carryOver);

            window.addEventListener('ask-history-save', (event) => {
                this.store(event.detail.id, event.detail.messages);
            });
        },

        /** Transcript the page load just retired server-side — file it under a new id. */
        archiveCarryOver(raw) {
            if (!raw) return;
            try {
                const messages = JSON.parse(raw);
                if (Array.isArray(messages) && messages.length) {
                    this.store(self.crypto.randomUUID(), messages);
                }
            } catch {
                // Corrupt payload — the transcript is not worth a broken page.
            }
        },

        readItems() {
            try {
                const parsed = JSON.parse(localStorage.getItem(this._key) || '{}');
                return Array.isArray(parsed.items) ? parsed.items : [];
            } catch {
                return [];
            }
        },

        /** Storage is a hard quota, so shed the oldest entries rather than throw. */
        writeItems() {
            for (let attempt = 0; attempt < 6; attempt++) {
                try {
                    localStorage.setItem(this._key, JSON.stringify({ v: 1, items: this.items }));
                    return;
                } catch {
                    if (!this.items.length) return;
                    this.items.pop();
                }
            }
        },

        /**
         * Same transcript, new id — a reload retires the server-side copy of a
         * conversation this browser already filed, and the ids never match
         * across page loads. Merge on content instead of stacking duplicates.
         */
        fingerprint(messages) {
            if (!Array.isArray(messages) || !messages.length) return '';
            return `${messages.length}|${messages[0].content}|${messages[messages.length - 1].content}`;
        },

        store(id, messages) {
            if (!id || !Array.isArray(messages) || !messages.length) return;
            const twin = this.items.find((item) => this.fingerprint(item.messages) === this.fingerprint(messages));
            const entryId = twin ? twin.id : id;
            const firstAsked = messages.find((message) => message.role === 'user');
            const entry = {
                id: entryId,
                title: (firstAsked || messages[0]).content.slice(0, 90),
                updatedAt: Date.now(),
                messages,
            };
            this.items = [entry, ...this.items.filter((item) => item.id !== entryId)].slice(0, 20);
            this.writeItems();
        },

        restore(item) {
            this.open = false;
            this.$wire.restoreConversation(item.id, item.messages);
        },

        remove(id) {
            this.items = this.items.filter((item) => item.id !== id);
            this.writeItems();
        },

        clearAll() {
            this.items = [];
            this.writeItems();
            this.open = false;
        },

        when(timestamp) {
            return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
        },
    }));

    /**
     * Paywall modal for monetized experts.
     * Usage: x-data="paywallModal"
     * Reads config from data-* attributes.
     */
    Alpine.data('paywallModal', () => ({
        open: false,
        checkoutUrl: '',
        paywallTracked: false,

        init() {
            this._referralSource = this.$el.dataset.referralSource || '';
            this._referralCategory = this.$el.dataset.referralCategory || '';
            this._expertToken = this.$el.dataset.expertToken || '';
            this._expertTitle = this.$el.dataset.expertTitle || '';
            this._expertCategory = this.$el.dataset.expertCategory || '';

            window.addEventListener('show-paywall', (e) => {
                this.open = true;
                this.checkoutUrl = e.detail.checkoutUrl;

                if (this._referralSource === 'explore' && !this.paywallTracked && typeof window.sageTrack === 'function') {
                    this.paywallTracked = true;
                    window.sageTrack('explore_paywall_shown', {
                        expert_token: this._expertToken,
                        expert_title: this._expertTitle,
                        category: this._expertCategory,
                        landing_category: this._referralCategory,
                    });
                }
            });
        },

        close() {
            this.open = false;
        },
    }));

    // =========================================================================
    // Chrome Extension Promotion Components
    // =========================================================================

    /**
     * Persistent header banner — shown on all authenticated pages until dismissed.
     * Usage: x-data="extensionPersistentBanner"
     * Reads extension ID from data-extension-id attribute.
     */
    Alpine.data('extensionPersistentBanner', () => ({
        visible: false,

        async init() {
            if (localStorage.getItem('sagetube_ext_banner_dismissed') === '1') return;
            const extensionId = this.$el.dataset.extensionId;
            if (!extensionId) return;
            const installed = await probeExtension(extensionId);
            this.visible = !installed;
        },

        dismiss() {
            localStorage.setItem('sagetube_ext_banner_dismissed', '1');
            this.visible = false;
        },
    }));

    /**
     * Post-save contextual banner — shown after manual video save if extension not installed.
     * Usage: x-data="extensionPostSaveBanner"
     * Reads extension ID from data-extension-id attribute.
     * Listens to 'resource-added' Livewire browser event.
     */
    Alpine.data('extensionPostSaveBanner', () => ({
        visible: false,

        init() {
            if (localStorage.getItem('sagetube_ext_postsave_dismissed') === '1') return;
            const extensionId = this.$el.dataset.extensionId;
            if (!extensionId) return;

            window.addEventListener('resource-added', async () => {
                const installed = await probeExtension(extensionId);
                if (!installed) this.visible = true;
            });
        },

        dismiss() {
            localStorage.setItem('sagetube_ext_postsave_dismissed', '1');
            this.visible = false;
        },
    }));

    // =========================================================================
    // Page-Specific Components
    // =========================================================================

    /**
     * Support chat widget with localStorage persistence.
     * Usage: x-data="supportChat"
     */
    Alpine.data('supportChat', () => ({
        isOpen: false,

        init() {
            this.isOpen = localStorage.getItem('supportChatOpen') === 'true';
            window.addEventListener('open-support-chat', () => {
                this.isOpen = true;
                localStorage.setItem('supportChatOpen', 'true');
            });
        },

        toggle() {
            this.isOpen = !this.isOpen;
            localStorage.setItem('supportChatOpen', this.isOpen);
        },
    }));

    /**
     * Citation expander for expert answers.
     * Usage: x-data="citationExpander"
     */
    Alpine.data('citationExpander', () => ({
        expandedCitations: {},

        toggleRef() {
            const ref = this.$el.dataset.ref;
            this.expandedCitations[ref] = !this.expandedCitations[ref];
        },

        get refExpanded() {
            const ref = this.$el.dataset.ref;
            return this.expandedCitations[ref] || false;
        },
    }));

    /**
     * Chart data component for usage page.
     * Usage: x-data="chartData"
     */
    Alpine.data('chartData', () => ({
        chart: null,
        data: null,

        init() {
            this.renderChart();
            window.addEventListener('chartDataUpdated', (event) => {
                this.updateChart(event.detail.data);
            });
        },

        renderChart() {
            if (this.data) {
                const canvas = document.getElementById('purposeChart');
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                if (this.chart) this.chart.destroy();
                this.chart = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels: this.data.labels,
                        datasets: [{
                            label: 'Amount (' + this.data.amount_unit + ')',
                            data: this.data.amount,
                            fill: false,
                        }],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                    },
                });
            }
        },

        updateChart(newData) {
            this.data = newData;
            this.renderChart();
        },
    }));

    /**
     * Pulse subscribe form for shared expert page.
     * Usage: x-data="pulseSubscribe"
     * Reads config from data-* attributes.
     */
    Alpine.data('pulseSubscribe', () => ({
        email: '',
        loading: false,
        subscribed: false,
        error: '',
        hasPulseChannels: false,

        init() {
            this.hasPulseChannels = this.$el.dataset.hasPulseChannels === 'true';
            this._subscribeUrl = this.$el.dataset.subscribeUrl || '';
            this._errorMessage = this.$el.dataset.errorMessage || 'Could not subscribe. Please try again.';
            this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
        },

        subscribe() {
            this.loading = true;
            this.error = '';
            fetch(this._subscribeUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': this._csrfToken,
                },
                body: JSON.stringify({ email: this.email }),
            })
                .then(r => {
                    if (!r.ok) throw r;
                    this.subscribed = true;
                })
                .catch(() => {
                    this.error = this._errorMessage;
                })
                .finally(() => { this.loading = false; });
        },
    }));

    /**
     * Bulk select with select-all checkbox for admin tables.
     * Usage: x-data="bulkSelect"
     * Reads ticket IDs from data-all-ids attribute (JSON array).
     */
    Alpine.data('bulkSelect', () => ({
        selectAll(event) {
            const allIds = JSON.parse(this.$el.dataset.allIds || '[]');
            if (event.target.checked) {
                this.$wire.set('selectedTickets', allIds);
            } else {
                this.$wire.set('selectedTickets', []);
            }
        },
    }));

    /**
     * Keyboard focus — slash key to focus an input.
     * Usage: x-data="keyboardFocus"
     */
    Alpine.data('keyboardFocus', () => ({
        init() {
            window.addEventListener('keydown', (e) => {
                if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
                    e.preventDefault();
                    this.$refs.searchInput.focus();
                }
            });
        },
    }));

    // =========================================================================
    // PowerGrid Components
    // =========================================================================

    /**
     * PowerGrid select-all checkbox with indeterminate state support.
     * Usage: x-data="pgCheckboxAll"
     * Listens for pg-checkbox-updated window event to recalculate state.
     */
    Alpine.data('pgCheckboxAll', () => ({
        init() {
            this.$nextTick(() => this.updateIndeterminate());
        },
        updateIndeterminate() {
            const checkbox = this.$refs.selectAllCheckbox;
            if (!checkbox) return;
            const rowCheckboxes = document.querySelectorAll('tbody input[type=checkbox]');
            const total = rowCheckboxes.length;
            const checked = Array.from(rowCheckboxes).filter(cb => cb.checked).length;
            const isIndeterminate = checked > 0 && checked < total;
            const isAllSelected = checked > 0 && checked === total;
            checkbox.indeterminate = isIndeterminate;
            checkbox.setAttribute('aria-checked', isIndeterminate ? 'mixed' : (isAllSelected ? 'true' : 'false'));
        },
        onCheckboxUpdated() {
            this.$nextTick(() => this.updateIndeterminate());
        },
    }));

    /**
     * PowerGrid detail row — entangles collapsed/collapseOthers from Livewire.
     * Usage: x-data="pgDetail"
     */
    Alpine.data('pgDetail', () => ({
        collapsed: false,
        collapseOthers: false,
        init() {
            this.collapsed = this.$wire.$entangle('show');
            this.collapseOthers = this.$wire.$entangle('collapseOthers');
        },
    }));

    /**
     * PowerGrid filter panel — entangles showFilters visibility from Livewire.
     * Usage: x-data="pgFilterToggle"
     * Binds: x-show="open"
     */
    Alpine.data('pgFilterToggle', () => ({
        open: false,
        init() {
            this.open = this.$wire.$entangle('showFilters', true);
        },
    }));

    /**
     * PowerGrid export/toggle-columns dropdown with entangled checkbox count.
     * Usage: x-data="pgExportToggle"
     * Binds: x-show="open", x-text="countLabel()"
     */
    Alpine.data('pgExportToggle', () => ({
        open: false,
        countChecked: [],
        init() {
            this.countChecked = this.$wire.$entangle('checkboxValues', true);
        },
        toggle() {
            this.open = !this.open;
        },
        close() {
            this.open = false;
        },
        countLabel() {
            return '(' + this.countChecked.length + ')';
        },
    }));

    /**
     * PowerGrid sortable column header — exposes sortable flag from data attribute.
     * Usage: x-data="pgSortable" data-sortable="true|false"
     */
    Alpine.data('pgSortable', () => ({
        sortable: false,
        init() {
            this.sortable = this.$el.dataset.sortable === 'true';
        },
    }));

    /**
     * PowerGrid soft-deletes dropdown — dispatches soft-delete events and closes.
     * Usage: x-data="pgSoftDeletes" data-table-name="..."
     */
    Alpine.data('pgSoftDeletes', () => ({
        open: false,
        _tableName: '',

        init() {
            this._tableName = this.$el.dataset.tableName || '';
        },

        toggle() {
            this.open = !this.open;
        },

        close() {
            this.open = false;
        },

        selectFilter(value) {
            this.$wire.dispatch('pg:softDeletes-' + this._tableName, { softDeletes: value });
            this.open = false;
        },
    }));

    /**
     * PowerGrid table base — tracks expanded row ID for detail toggle coordination.
     * Usage: x-data="pgTableBase"
     */
    Alpine.data('pgTableBase', () => ({
        expandedId: null,
    }));

    /**
     * PowerGrid batch export completed panel — starts open, toggleable.
     * Usage: x-data="pgBatchExport"
     * Binds: x-show="show", x-on:click="toggle"
     */
    Alpine.data('pgBatchExport', () => ({
        show: true,
        toggle() {
            this.show = !this.show;
        },
    }));

    /**
     * PowerGrid toggle-detail row cell — per-row expand/collapse with cross-row coordination.
     * Usage: x-data="pgToggleDetail"
     *        data-table-name="..." data-row-id="..." data-collapse-others="true|false"
     * Listens to pg-toggle-detail-{tableName}-hidden-all and pg-toggle-detail-{tableName}-loaded
     * window events via data-attribute-driven handlers registered in init().
     */
    Alpine.data('pgToggleDetail', () => ({
        collapsed: false,
        loading: false,
        collapseOthers: false,
        _tableName: '',
        _rowId: '',

        init() {
            this._tableName = this.$el.dataset.tableName || '';
            this._rowId = this.$el.dataset.rowId || '';
            this.collapseOthers = this.$el.dataset.collapseOthers === 'true';

            this._onHideAll = () => { this.collapsed = false; };
            this._onLoaded = () => { this.loading = false; };

            window.addEventListener('pg-toggle-detail-' + this._tableName + '-hidden-all', this._onHideAll);
            window.addEventListener('pg-toggle-detail-' + this._tableName + '-loaded', this._onLoaded);
        },

        destroy() {
            window.removeEventListener('pg-toggle-detail-' + this._tableName + '-hidden-all', this._onHideAll);
            window.removeEventListener('pg-toggle-detail-' + this._tableName + '-loaded', this._onLoaded);
        },

        toggleDetail() {
            const isOpen = this.collapsed;

            if (this.collapseOthers) {
                this.$dispatch('pg-toggle-detail-' + this._tableName + '-hidden-all');
                // Update expandedId on the parent pgTableBase scope
                const parentScope = this.$el.closest('[x-data="pgTableBase"]');
                if (parentScope && parentScope._x_dataStack) {
                    const data = parentScope._x_dataStack[0];
                    if (data) data.expandedId = this._rowId;
                }
            }

            this.loading = true;
            this.collapsed = !isOpen;

            this.$dispatch('pg-toggle-detail-' + this._tableName + '-' + this._rowId, {
                collapsed: this.collapsed,
            });
        },

        isExpanded(rowId) {
            if (!this.collapseOthers) return this.collapsed;
            const parentScope = this.$el.closest('[x-data="pgTableBase"]');
            if (!parentScope || !parentScope._x_dataStack) return this.collapsed;
            const data = parentScope._x_dataStack[0];
            return this.collapsed && data && data.expandedId === rowId;
        },
    }));

});
